import React from "react";
import { Paper, Box, Typography, Button, alpha, useTheme } from "@mui/material";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CloseIcon from "@mui/icons-material/Close";
import { EvidenceTab } from "../hooks/useListVideos";

interface EmptyVideosStateProps {
  // Which evidence tab is empty; the not-fetched tab gets its own copy.
  tab?: EvidenceTab;
  hasActiveFilters: boolean;
  onResetFilters: () => void;
}

export const EmptyVideosState: React.FC<EmptyVideosStateProps> = ({
  tab,
  hasActiveFilters,
  onResetFilters,
}) => {
  const theme = useTheme();
  const isUnfetchedTab = tab === "unfetched";

  // No-filter empty-state copy, tailored per tab.
  const emptyTitle = isUnfetchedTab
    ? "No other footage"
    : tab === "mine"
      ? "No videos uploaded by you yet"
      : tab === "shared"
        ? "No evidence shared with you yet"
        : "No videos yet";
  const emptyBody = isUnfetchedTab
    ? "Videos the system set aside will appear here. You can add any of them to make them available for reports."
    : tab === "mine"
      ? "Bodycam footage you upload will appear here."
      : tab === "shared"
        ? "Footage uploaded by other officers in your station will appear here."
        : "Upload bodycam footage to get started with report generation.";

  return (
    <Paper
      sx={{
        p: { xs: 4, md: 6 },
        textAlign: "center",
        bgcolor: alpha(theme.palette.background.paper, 0.5),
        borderRadius: 3,
        border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 400,
      }}
      elevation={0}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          borderRadius: "50%",
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 3,
        }}
      >
        <VideocamOffIcon
          sx={{
            fontSize: 50,
            color: alpha(theme.palette.primary.main, 0.6),
          }}
        />
      </Box>
      <Typography
        variant="h5"
        fontWeight={600}
        color="text.primary"
        gutterBottom
      >
        {hasActiveFilters ? "No matching videos found" : emptyTitle}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 450, mx: "auto", mb: 4 }}
      >
        {hasActiveFilters
          ? "Try adjusting your filters or search criteria to find what you're looking for."
          : emptyBody}
      </Typography>
      {hasActiveFilters && (
        <Button
          variant="outlined"
          onClick={onResetFilters}
          startIcon={<CloseIcon />}
          sx={{
            textTransform: "none",
            px: 3,
            py: 1,
          }}
        >
          Clear all filters
        </Button>
      )}
    </Paper>
  );
};
